import React from "react";
import { motion } from "framer-motion";
import Toggle from "../../../components/common/Toggle";
import Badge from "../../../components/common/Badge";
import {
  formatDeviceInfo,
  getPlatformIcon,
} from "../../../services/deviceService";
import {
  DeviceMobileIcon,
  UserIcon,
  CalendarIcon,
  CogIcon,
  BellIcon,
  MoonIcon,
  ChartBarIcon,
} from "@heroicons/react/outline";

const DeviceCard = ({ device, onToggle, onEditPrefs }) => {
  if (!device) return null;

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatQuietHours = (quietHours) => {
    if (!quietHours || quietHours.start === undefined) return "Not set";
    return `${quietHours.start}:00 - ${quietHours.end}:00`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-4 sm:p-6 overflow-hidden"
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-2 sm:gap-3 overflow-hidden">
          <div className="text-xl sm:text-2xl flex-shrink-0">
            {getPlatformIcon(device.platform)}
          </div>
          <div className="min-w-0 overflow-hidden">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base truncate">
              {device.platform || "Unknown"} Device
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
              {device._id}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0 ml-2">
          <Toggle
            enabled={Boolean(device.isActive)}
            onChange={(enabled) => onToggle(device._id, enabled)}
          />
        </div>
      </div>

      {/* Device Info */}
      <div className="space-y-2 sm:space-y-3">
        {/* User Info */}
        <div className="flex items-center gap-2 text-xs sm:text-sm min-w-0">
          <UserIcon className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
          <span className="text-gray-600 dark:text-gray-300 truncate">
            {device.customerId?.name || device.customerId || "Unknown User"}
            {device.isAnonymous && (
              <span className="ml-2 text-amber-500">(Anonymous)</span>
            )}
          </span>
        </div>

        {/* App Info */}
        {device.appInfo && (
          <div className="flex items-center gap-2 text-xs sm:text-sm min-w-0">
            <DeviceMobileIcon className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
            <span className="text-gray-600 dark:text-gray-300 truncate">
              {device.appInfo.model || "Unknown Model"} •
              {device.appInfo.os || "Unknown OS"} • v
              {device.appInfo.version || "?"}
              {device.appInfo.build && ` (Build ${device.appInfo.build})`}
            </span>
          </div>
        )}

        {/* Preferences */}
        <div className="flex items-center gap-2 flex-wrap">
          <BellIcon className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
          <div className="flex gap-1 sm:gap-2 flex-wrap">
            <Badge
              variant={device.prefs?.promoPush ? "success" : "neutral"}
              size="sm"
            >
              Push: {device.prefs?.promoPush ? "On" : "Off"}
            </Badge>
            <Badge
              variant={device.prefs?.promoWhatsApp ? "success" : "neutral"}
              size="sm"
            >
              WhatsApp: {device.prefs?.promoWhatsApp ? "On" : "Off"}
            </Badge>
          </div>
        </div>

        {/* Quiet Hours */}
        <div className="flex items-center gap-2 text-xs sm:text-sm min-w-0">
          <MoonIcon className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
          <span className="text-gray-600 dark:text-gray-300 truncate">
            Quiet Hours: {formatQuietHours(device.quietHours)}
          </span>
        </div>

        {/* Stats - New section to display notification stats from backend */}
        {device.stats && (
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2 sm:p-3 mt-2">
            <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
              <ChartBarIcon className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
              <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                Notification Stats
              </h4>
            </div>
            <div className="grid grid-cols-3 gap-1 sm:gap-2 text-center">
              <div>
                <p className="text-xs text-gray-500 truncate">Delivered</p>
                <p className="text-xs sm:text-sm font-medium">
                  {device.stats?.delivered || 0}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 truncate">Opened</p>
                <p className="text-xs sm:text-sm font-medium">
                  {device.stats?.opened || 0}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 truncate">Clicked</p>
                <p className="text-xs sm:text-sm font-medium">
                  {device.stats?.clicked || 0}
                </p>
              </div>
            </div>
            {device.stats?.lastOpenedAt && (
              <p className="text-xs text-gray-500 mt-1 truncate">
                Last opened: {formatDate(device.stats.lastOpenedAt)}
              </p>
            )}
          </div>
        )}

        {/* Device Metadata - New section */}
        {device.deviceMetadata && (
          <div className="text-xs text-gray-500 mt-1 truncate">
            {device.deviceMetadata.manufacturer &&
              `${device.deviceMetadata.manufacturer} • `}
            {device.deviceMetadata.screenSize &&
              `${device.deviceMetadata.screenSize} • `}
            {device.deviceMetadata.timezone || device.deviceMetadata.carrier}
          </div>
        )}

        {/* Timestamps */}
        <div className="flex flex-col xs:flex-row xs:justify-between text-xs text-gray-500 pt-2 border-t border-gray-200 dark:border-gray-700 space-y-1 xs:space-y-0">
          <span className="truncate">
            Last Active: {formatDate(device.lastActiveAt)}
          </span>
          <span className="truncate">Locale: {device.locale || "en-US"}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 flex justify-end">
        <button
          onClick={() => onEditPrefs(device)}
          className="text-xs sm:text-sm text-primary hover:text-primary-dark"
        >
          <CogIcon className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
          Edit Preferences
        </button>
      </div>
    </motion.div>
  );
};

export default DeviceCard;
